import { broadcastLaborOpenSessionChanged } from "./laborOpenSessionSync";
import { getLaborTabId } from "./laborTabId";

const STORAGE_KEY = "integrator_hub_labor_dev_shell_v1";

/** Same-tab listeners: localStorage `storage` event does not fire in the writing document. */
export const LABOR_DEV_SHELL_CHANGED_EVENT = "integrator-hub-labor-dev-shell-changed";

/** Storage key exposed so `storage` event listeners in other tabs can filter. */
export const LABOR_DEV_SHELL_STORAGE_KEY = STORAGE_KEY;

export interface LaborDevShellLockPayload {
  page: string;
  /** Tab id of the writer. May not equal the reader's tab id (cross-tab presence). */
  tabId: string;
}

function dispatchShellChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(LABOR_DEV_SHELL_CHANGED_EVENT));
}

/**
 * Persists "the user is clocked in on this working page" for local-only labor (dev persistence
 * off / writes blocked) so the hub shell can lock navigation the same way as a real
 * `labor.device_session` row. Stored in `localStorage` so other tabs of the same browser see
 * the lock and the lockdown gate renders the interstitial there too.
 */
export function setLaborDevShellLock(input: { page: string }): void {
  if (typeof localStorage === "undefined") return;
  const page = input.page.trim();
  if (page === "") return;
  const tabId = getLaborTabId();
  const payload: LaborDevShellLockPayload = { page, tabId };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  dispatchShellChanged();
}

export function clearLaborDevShellLock(options?: { notifyOtherTabs?: boolean }): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
  dispatchShellChanged();
  if (options?.notifyOtherTabs !== false) {
    broadcastLaborOpenSessionChanged();
  }
}

/**
 * Returns the persisted lock as-is. The reader does NOT filter by tab id; the lockdown gate
 * compares the lock's `tabId` to the current tab id and locks the destination route when they
 * differ. Filtering here would hide cross-tab presence from the gate.
 */
export function readLaborDevShellLock(): LaborDevShellLockPayload | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null || raw.trim() === "") return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const record = parsed as Record<string, unknown>;
    const page = typeof record.page === "string" ? record.page.trim() : "";
    const tabId = typeof record.tabId === "string" ? record.tabId.trim() : "";
    if (page === "" || tabId === "") return null;
    return { page, tabId };
  } catch {
    return null;
  }
}
